import { TrackedEvents, trackEvent } from 'analytics';
import { type AttachmentDetails, type DetectedUbcContent, Evidence, useApiTradeV1SiteMaterialsEvidenceList } from 'api';
import { dayjs } from 'date';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  CenteredLoader,
  Chip,
  DataCard,
  ExpandableIconButton,
  formatNumber,
  Grid,
  MediaDialog,
  ProgressiveImage,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from 'ui';
import { ExpandMore, ImageOutlined, Scale } from 'ui/icons';

import { TargetBaleWeightsHistogram } from '../../../charts';
import { AiFiltersAndBreakdownSidebar } from '../../../evidence';
import { AnnotatedMediaItem, useAnnotations } from '../../../quality-report';
import { IGNORE_BALE_LABELS } from '../../../quality-report/constants';

type TabValue = 'typical' | 'best';

const MAX_IMAGE_SHOWN = 3;
const model = 'alu';

const ATTACHMENT_DETAILS_KEY_MAP: Record<
  keyof AttachmentDetails,
  {
    label: string;
    shouldDisplay: boolean;
  }
> = {
  dirt_score: {
    label: 'Dirt Score',
    shouldDisplay: false,
  },
  foil_and_tray: {
    label: 'Foil and Tray',
    shouldDisplay: true,
  },
  food_can: {
    label: 'Food Can',
    shouldDisplay: true,
  },
  non_metal: {
    label: 'Non-Metal',
    shouldDisplay: true,
  },
  other_aluminium: {
    label: 'Other Aluminium',
    shouldDisplay: true,
  },
  other_metal: {
    label: 'Other Metal',
    shouldDisplay: true,
  },
};

const getTargetGrade = (grade: string): number => {
  return Number(grade.split('/')[0]);
};

const Callout = ({
  percentage,
  title,
  variant = 'primary',
}: {
  percentage: string;
  title: string;
  variant: 'primary' | 'secondary';
}) => {
  const theme = useTheme();

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        backgroundColor:
          variant === 'primary' ? `${theme.palette.primary.main}15` : `${theme.palette.secondary.main}05`,
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main}
      >
        {percentage}
      </Typography>
      <Typography
        variant="body1"
        color={variant === 'primary' ? theme.palette.primary.main : theme.palette.secondary.main}
      >
        {title}
      </Typography>
    </Alert>
  );
};

export const TargetBaleWeightsDataCard = ({
  isLoading,
  detectedUbcContent,
  grade,
  siteMaterialId,
}: {
  isLoading?: boolean;
  detectedUbcContent: DetectedUbcContent;
  grade: string;
  siteMaterialId: string;
}) => {
  const targetGrade = getTargetGrade(grade);
  const today = dayjs();
  const ninetyDaysAgo = today.subtract(90, 'days');
  const [activeTab, setActiveTab] = useState<TabValue>('typical');

  const { data: baleEvidence, isLoading: isEvidenceLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    {
      include_ids:
        activeTab === 'typical'
          ? detectedUbcContent.typical_load_evidence_ids
          : detectedUbcContent.best_load_evidence_ids,
    },
    {
      query: {
        enabled:
          Boolean(siteMaterialId) && activeTab === 'typical'
            ? detectedUbcContent.typical_load_evidence_ids.length > 0
            : detectedUbcContent.best_load_evidence_ids.length > 0,
      },
    }
  );

  const handleChangeTab = (_event: React.SyntheticEvent, value: TabValue) => {
    setActiveTab(value);
  };

  const averageUbcContent = formatNumber(detectedUbcContent.average_ubc_content * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });
  const attachmentContent = formatNumber(100 - detectedUbcContent.average_ubc_content * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });
  const typicalLoadLowRange = formatNumber(detectedUbcContent.typical_load_low_range * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });
  const typicalLoadHighRange = formatNumber(detectedUbcContent.typical_load_high_range * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });

  const areBestRangesEqual = detectedUbcContent.best_load_low_range === detectedUbcContent.best_load_high_range;

  const bestLoadLowRange = formatNumber(detectedUbcContent.best_load_low_range * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });
  const bestLoadHighRange = formatNumber(detectedUbcContent.best_load_high_range * 100, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: 'percent',
  });

  const {
    selectedAnnotation,
    handleImageChange,
    hiddenCategories,
    handleHideCategory,
    handleToggleLegend,
    showLegend,
    showSidebar,
  } = useAnnotations({});

  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);

  const carouselMedia = baleEvidence?.results.map((evidence) => String(evidence.file || evidence.preview)) ?? [];

  const handleSelect = (src: string) => {
    const relatedMedia = baleEvidence?.results.find((file) => file.file === src || file.preview === src);
    setSelectedEvidence(relatedMedia || null);
    handleImageChange([...(relatedMedia?.model_results?.coco_prediction || [])]);
  };

  const showSwitch = Boolean(selectedAnnotation?.some(({ label }) => !IGNORE_BALE_LABELS.includes(label)));

  const navigate = useNavigate();
  const handleClickMedia = () => {
    trackEvent(TrackedEvents.QUALITY_REPORT_CLICK_CATEGORY_IMAGES, {
      category: 'AI Bale',
    });
    navigate('media?tab=accepted');
  };

  return (
    <>
      <DataCard
        isLoading={isLoading}
        slotProps={{
          header: {
            action: <ExpandableIconButton icon={<ImageOutlined />} label="Show media" onClick={handleClickMedia} />,
            avatar: <Scale />,
            title: 'AI Bale Analysis',
          },
        }}
        dataTestId="target-bale-weights-data-card"
      >
        <DataCard.Item colSpan={6}>
          <Callout percentage={averageUbcContent} title="Average UBC Weight Content" variant="primary" />
        </DataCard.Item>
        <DataCard.Item colSpan={6}>
          <Callout percentage={attachmentContent} title="Attachment Weight Content" variant="secondary" />
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Typography color="textSecondary">
            Inspectors have gathered data from analysing{' '}
            <strong>{detectedUbcContent.target_bale_weights.length}</strong> bales from&nbsp;
            <strong>{ninetyDaysAgo.format('MMMM YYYY')}</strong> and <strong>{today.format('MMMM YYYY')}</strong>
          </Typography>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Accordion defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" fontWeight="bold">
                Attachment details
              </Typography>
            </AccordionSummary>
            <Box display="flex" flexWrap="wrap" gap={1} padding={2}>
              {Object.entries(detectedUbcContent.attachment_details).map(([key, value]) =>
                ATTACHMENT_DETAILS_KEY_MAP[key as keyof AttachmentDetails].shouldDisplay ? (
                  <Chip
                    key={key}
                    label={`${ATTACHMENT_DETAILS_KEY_MAP[key as keyof AttachmentDetails].label} ${formatNumber(
                      Number(value) * 100,
                      {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: 'percent',
                      }
                    )}`}
                    size="small"
                  />
                ) : null
              )}
            </Box>
          </Accordion>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" fontWeight="bold">
                Show bale quality data
              </Typography>
            </AccordionSummary>
            <TargetBaleWeightsHistogram
              targetBaleWeights={detectedUbcContent.target_bale_weights}
              targetGrade={targetGrade}
            />
          </Accordion>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleChangeTab}>
              <Tab
                label={
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Typical bales
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {typicalLoadLowRange} - {typicalLoadHighRange} UBC
                    </Typography>
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                }}
                value="typical"
                wrapped
              />
              <Tab
                label={
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Best bales
                    </Typography>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {areBestRangesEqual ? bestLoadLowRange : `${bestLoadLowRange} - ${bestLoadHighRange}`}
                    </Typography>
                  </Box>
                }
                sx={{
                  textTransform: 'none',
                }}
                value="best"
                wrapped
              />
            </Tabs>
          </Box>
        </DataCard.Item>
        <DataCard.Item colSpan={12}>
          <Grid container>
            {isEvidenceLoading ? (
              <Grid item xs={12}>
                <CenteredLoader />
              </Grid>
            ) : null}
            {baleEvidence?.results.map((evidence, index) => {
              if (index >= MAX_IMAGE_SHOWN) return null;

              return (
                <Grid item xs={4} key={index}>
                  <ProgressiveImage
                    key={index}
                    rounded={true}
                    src={String(evidence.file)}
                    alt="evidence photo"
                    loading="lazy"
                    placeholderSrc={String(evidence.preview || evidence.file)}
                    onClick={() => handleSelect(String(evidence.file))}
                    zoomOnHover={true}
                    sx={{
                      cursor: 'pointer',
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </DataCard.Item>
      </DataCard>

      <MediaDialog
        open={Boolean(selectedEvidence)}
        selectedMedium={selectedEvidence?.file || ''}
        onClose={() => setSelectedEvidence(null)}
        media={carouselMedia}
        handleNavigate={handleSelect}
        slotProps={{
          imageCarousel: {
            slotProps: {
              ...(selectedAnnotation
                ? {
                    mediaItemSlot: (
                      <AnnotatedMediaItem
                        image={selectedEvidence?.file || ''}
                        annotations={selectedAnnotation}
                        hiddenCategories={hiddenCategories}
                        showLegend={showLegend}
                        model={model}
                      />
                    ),
                    sidebar: showSidebar
                      ? {
                          children: (
                            <AiFiltersAndBreakdownSidebar
                              selectedAnnotation={selectedAnnotation}
                              hiddenCategories={hiddenCategories}
                              handleHideCategory={handleHideCategory}
                              handleToggleLegend={handleToggleLegend}
                              showLegend={showLegend}
                              showSwitch={showSwitch}
                              model={model}
                              attachmentDetails={selectedEvidence?.attachment_details ?? undefined}
                              targetWeightPercentage={selectedEvidence?.target_weight_percentage ?? undefined}
                            />
                          ),
                          label: 'Adjust AI Filters',
                        }
                      : undefined,
                  }
                : null),
            },
          },
        }}
      />
    </>
  );
};
